import type {PayloadAction} from '@reduxjs/toolkit'
import {createSlice} from '@reduxjs/toolkit'

// --- Types ---
export type Pipeline = {buildJob: string | {id: string; label: string}; buildDurationThreshold: string}
export type Deploy = {
  deployJob: string | {id: string; label: string}
  deployProdBranch: string
  deployHotfixBranch: string
  deployProdEnvSuffix: string
}
export type Repo = {repoUrl: string | {id: string; label: string; fullObject?: any}; branches: string[]}
export type AppSec = {url: string | {id: string; label: string; fullObject?: any}}
export type Security = {staticAnalysisJob: string | {id: string; label: string; fullObject?: any}; sonarBranch: string}
export type QAMetric = {
  organization?: string
  projectName?: string
  adoBoard: string | {id: string; label: string; fullObject?: any}
  buildPipeline: string | {id: string; label: string; fullObject?: any}
  collectorItemId?: string
}
export type Feature = {
  teamName: string | {id: string; label: string; fullObject: any}
  reusabilityBaselinePeriod: string
  reusableStoryPoints: string
  automatedBaselinePeriod: string
  automatedTestCases: string
  totalTestCases: string
  automationCandidateTestCases: string
  reusabilityTags: string
  programTags: string
  totalStoryPoints: string
  onTimeDelivery: string
  commitmentMet: string
  velocity: string
  defectDensity: string
  automationEfficiency: string
  testAutomation: string
  overallAutomationCoverage: string
  organization: string
  projectName: string
}

// --- Onboarding Dashboard Info ---
type Owner = {
  ownerName: string
  emails: string
}

interface Dashboard {
  title?: string
  owners?: Owner[]
  application?: any
  teamInfo?: {
    teamName?: string
  }
  configurationItemBusAppName?: string
}

export interface OnboardingDashboardState {
  dashboardId: string | null
  component: any | null
  editMode: boolean
  dashboard?: Dashboard
  [key: string]: any
}

export const initialState: OnboardingDashboardState = {
  dashboardId: null,
  component: null,
  editMode: false,
  teamName: '',
  ownerName: '',
  email: '',
  scoreEnabled: false,
  teamDescription: '',
  dashboard: undefined,
} as OnboardingDashboardState

const onboardingDashboardSlice = createSlice({
  name: 'onboardingDashboard',
  initialState,
  reducers: {
    setOnboardingDashboardInfo: (state, action: PayloadAction<Partial<OnboardingDashboardState>>) => {
      return {...state, ...action.payload}
    },
    clearOnboardingDashboardInfo: () => ({dashboardId: null, component: null, editMode: false}),
  },
})
export const {setOnboardingDashboardInfo, clearOnboardingDashboardInfo} = onboardingDashboardSlice.actions
export const onboardingDashboardReducer = onboardingDashboardSlice.reducer

// --- Build ---
interface BuildState {
  buildDurationThreshold: string
  pipelines: Pipeline[]
}

export const initialBuildState: BuildState = {
  buildDurationThreshold: '',
  pipelines: [],
}

const buildSlice = createSlice({
  name: 'build',
  initialState: initialBuildState,
  reducers: {
    setBuildPipelines: (state, action: PayloadAction<Pipeline[]>) => {
      state.pipelines = action.payload
    },
    setBuildDurationThreshold: (state, action: PayloadAction<string>) => {
      state.buildDurationThreshold = action.payload
    },
    setBuild: (state, action: PayloadAction<BuildState>) => {
      state.pipelines = action.payload.pipelines
      state.buildDurationThreshold = action.payload.buildDurationThreshold
    },
  },
})

export const {setBuildPipelines, setBuildDurationThreshold, setBuild} = buildSlice.actions
export const buildReducer = buildSlice.reducer

// --- Deploy ---
const initialDeploy: Deploy = {
  deployJob: '',
  deployProdBranch: '',
  deployHotfixBranch: '',
  deployProdEnvSuffix: '',
}
const deploySlice = createSlice({
  name: 'deploy',
  initialState: [initialDeploy] as Deploy[],
  reducers: {
    setDeployPipelines: (state, action: PayloadAction<Deploy[]>) => action.payload,
  },
})
export const {setDeployPipelines} = deploySlice.actions
export const deployReducer = deploySlice.reducer

// --- Repo ---
const repoSlice = createSlice({
  name: 'repo',
  initialState: [{repoUrl: '', branches: ['']}] as Repo[],
  reducers: {
    setRepos: (state, action: PayloadAction<Repo[]>) => {
      return action.payload.map(repo => ({
        ...repo,
        branches: Array.isArray((repo as any).branches) ? [...(repo as any).branches] : [''],
      }))
    },
  },
})
export const {setRepos} = repoSlice.actions
export const repoReducer = repoSlice.reducer

const appsecSlice = createSlice({
  name: 'appsec',
  initialState: [] as AppSec[],
  reducers: {
    setAppsecs: (state, action: PayloadAction<AppSec[]>) => action.payload,
  },
})
export const {setAppsecs} = appsecSlice.actions
export const appsecReducer = appsecSlice.reducer

// --- Security ---
const securitySlice = createSlice({
  name: 'security',
  initialState: [] as Security[],
  reducers: {
    setSecurities: (state, action: PayloadAction<Security[]>) => action.payload,
  },
})
export const {setSecurities} = securitySlice.actions
export const securityReducer = securitySlice.reducer

// --- QA ---
const initialQAMetric: QAMetric = {adoBoard: '', buildPipeline: ''}
const qaSlice = createSlice({
  name: 'qa',
  initialState: [initialQAMetric] as QAMetric[],
  reducers: {
    setQAMetrics: (state, action: PayloadAction<QAMetric[]>) => action.payload,
  },
})
export const {setQAMetrics} = qaSlice.actions
export const qaReducer = qaSlice.reducer

// --- Feature ---
const featureSlice = createSlice({
  name: 'feature',
  initialState: [] as Feature[],
  reducers: {
    setFeatures: (state, action: PayloadAction<Feature[]>) => action.payload,
  },
})
export const {setFeatures} = featureSlice.actions
export const featureReducer = featureSlice.reducer

// --- V2: Code Analysis (replaces Security in the v2 onboarding flow) ---
export type CodeAnalysis = {
  staticAnalysisJob: string | {id: string; label: string; fullObject?: any}
  sonarBranch: string
}
const codeAnalysisSlice = createSlice({
  name: 'codeAnalysisV2',
  initialState: [] as CodeAnalysis[],
  reducers: {
    setCodeAnalysis: (state, action: PayloadAction<CodeAnalysis[]>) => action.payload,
  },
})
export const {setCodeAnalysis} = codeAnalysisSlice.actions
export const codeAnalysisReducer = codeAnalysisSlice.reducer
